import { createContext, useContext } from "react";
import type { SupportedLanguage } from "../../highlight";
import CodeBlock from "../code-block";
import PredictionsCancelDefaultBash from "./snippets/predictions.cancel.default/bash.mdx";
import PredictionsCancelDefaultJavascript from "./snippets/predictions.cancel.default/javascript.mdx";
import PredictionsCancelDefaultPython from "./snippets/predictions.cancel.default/python.mdx";
import PredictionsCreateDefaultBash from "./snippets/predictions.create.default/bash.mdx";
import PredictionsCreateDefaultJavascript from "./snippets/predictions.create.default/javascript.mdx";
import PredictionsCreateDefaultPython from "./snippets/predictions.create.default/python.mdx";
import PredictionsCreateStreamBash from "./snippets/predictions.create.stream/bash.mdx";
import PredictionsCreateStreamJavascript from "./snippets/predictions.create.stream/javascript.mdx";
import PredictionsCreateStreamPython from "./snippets/predictions.create.stream/python.mdx";
import PredictionsCreateWebhookBash from "./snippets/predictions.create.webhook/bash.mdx";
import PredictionsCreateWebhookJavascript from "./snippets/predictions.create.webhook/javascript.mdx";
import PredictionsCreateWebhookPython from "./snippets/predictions.create.webhook/python.mdx";
import PredictionsGetDefaultBash from "./snippets/predictions.get.default/bash.mdx";
import PredictionsGetDefaultJavascript from "./snippets/predictions.get.default/javascript.mdx";
import PredictionsGetDefaultPython from "./snippets/predictions.get.default/python.mdx";
import PredictionsListDefaultBash from "./snippets/predictions.list.default/bash.mdx";
import PredictionsListDefaultJavascript from "./snippets/predictions.list.default/javascript.mdx";
import PredictionsListDefaultPython from "./snippets/predictions.list.default/python.mdx";
import PredictionsListPaginationBash from "./snippets/predictions.list.pagination/bash.mdx";
import PredictionsListPaginationJavascript from "./snippets/predictions.list.pagination/javascript.mdx";
import PredictionsListPaginationPython from "./snippets/predictions.list.pagination/python.mdx";

export const GettingStartedContext = createContext({});

function template(str: string, o: Record<string, any>): string {
  return str.replace(/\{\{\s*([^\s{}]+)\s*\}\}/g, (a, b) => {
    if (!(b in o)) {
      throw new Error(`Missing template value: {{ ${b} }}`);
    }
    const r = o[b];
    return typeof r === "string" ? r : typeof r === "number" ? r.toString() : a;
  });
}

export function MDXWrapper({ children }: React.PropsWithChildren) {
  return <div className="mdx-content">{children}</div>;
}

/**
 * Renders the children provided or will hide them if `hidden === true`.
 */
export function MDXSection({
  show,
  children,
}: React.PropsWithChildren<{ show: boolean }>) {
  return show ? children : null;
}

// This is totally hacky, but it's the simplest way of getting MDX
// to interpolate REPLICATE_TEMPLATE_VARIABLES into the code snippets.
// REPLICATE_PREDICTION_ID
export function MDXPre(props: any) {
  const ctx = useContext(GettingStartedContext);

  // Big assumptions baked into this, but it's a start!
  const { children } = props;

  let language: SupportedLanguage | undefined;
  let code: string | undefined;
  if (typeof children === "string") {
    code = children;
  } else if (
    Array.isArray(children) &&
    children.every((x) => typeof x === "string")
  ) {
    code = children.join("");
  } else if (children.props) {
    const { children: innerChildren, className } = children.props;
    language = className?.split("-")?.[1];
    code = innerChildren;
  }

  if (typeof code !== "string") {
    throw new Error("API Changed");
  }

  const replacedCode = template(code, ctx);

  return (
    <div
      // Please don't remove this class –
      // it's used to ensure Tailwind's prose styles
      // don't get applied to the code block which
      // has its own r8 styles baked in.
      className="not-prose"
    >
      <CodeBlock language={language} textContent={replacedCode} />
    </div>
  );
}

function transformSnippetComponent(SnippetMdxComponent: any) {
  const gsp = useContext(GettingStartedContext);

  return (
    <SnippetMdxComponent
      {...gsp}
      components={{
        pre: (props) => (
          <MDXPre {...props} REPLICATE_PREDICTION_ID="123456789" />
        ),
      }}
    />
  );
}

function transformSnippet(object: {
  javascript: React.Component;
  bash: React.Component;
  python: React.Component;
}) {
  const mapped: Partial<Record<keyof typeof object, React.ReactNode>> = {};
  for (const key of ["javascript", "bash", "python"]) {
    mapped[key] = object[key] ? (
      transformSnippetComponent(object[key])
    ) : (
      <MDXPre>No snippet defined for {key}</MDXPre>
    );
  }
  return mapped as Record<keyof typeof object, React.ReactNode>;
}

const snippetMap = {
  "predictions.create.default": {
    bash: PredictionsCreateDefaultBash,
    javascript: PredictionsCreateDefaultJavascript,
    python: PredictionsCreateDefaultPython,
  },
  "predictions.create.stream": {
    bash: PredictionsCreateStreamBash,
    javascript: PredictionsCreateStreamJavascript,
    python: PredictionsCreateStreamPython,
  },
  "predictions.create.webhook": {
    bash: PredictionsCreateWebhookBash,
    javascript: PredictionsCreateWebhookJavascript,
    python: PredictionsCreateWebhookPython,
  },
  "predictions.get.default": {
    bash: PredictionsGetDefaultBash,
    javascript: PredictionsGetDefaultJavascript,
    python: PredictionsGetDefaultPython,
  },
  "predictions.cancel.default": {
    bash: PredictionsCancelDefaultBash,
    javascript: PredictionsCancelDefaultJavascript,
    python: PredictionsCancelDefaultPython,
  },
  "predictions.list.default": {
    bash: PredictionsListDefaultBash,
    javascript: PredictionsListDefaultJavascript,
    python: PredictionsListDefaultPython,
  },
  "predictions.list.pagination": {
    bash: PredictionsListPaginationBash,
    javascript: PredictionsListPaginationJavascript,
    python: PredictionsListPaginationPython,
  },
} as const;

export function snippets(key: string) {
  return transformSnippet(snippetMap[key] ?? {});
}
